import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { launchColors, overlineSx } from '../launchTheme';
import CopyButton from '../ui/CopyButton';
import { counterColor } from './alcoholLabels';

interface Props {
  label: string;
  value: string;
  limit: number;
  minRows?: number;
  canEdit: boolean;
  promptReady: boolean;
  sourceText?: string;
  generating: boolean;
  suggestion?: string;
  suggestionNote?: string;
  onGenerate: (draft: string) => void;
  onSave: (text: string) => void;
  onUseSuggestion: (text: string) => void;
  onDismissSuggestion: () => void;
  onHistory: () => void;
  onTooLong?: (tooLong: boolean) => void;
  onCopied?: (ok: boolean) => void;
}

const Counter: React.FC<{ length: number; limit: number }> = ({ length, limit }) => (
  <Typography component="span" sx={{ fontSize: 12, fontWeight: length > limit ? 700 : 400, color: counterColor(length, limit), fontVariantNumeric: 'tabular-nums' }}>
    {length}/{limit}
  </Typography>
);

const GeneratedTextField: React.FC<Props> = ({
  label,
  value,
  limit,
  minRows = 3,
  canEdit,
  promptReady,
  sourceText,
  generating,
  suggestion,
  suggestionNote,
  onGenerate,
  onSave,
  onUseSuggestion,
  onDismissSuggestion,
  onHistory,
  onTooLong,
  onCopied,
}) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const tooLong = draft.length > limit;
  const onTooLongRef = useRef(onTooLong);
  onTooLongRef.current = onTooLong;
  useEffect(() => { onTooLongRef.current?.(tooLong); }, [tooLong]);
  const source = sourceText ?? draft;
  const generateBlocked = !source.trim()
    ? (sourceText === undefined ? 'Rutan är tom.' : 'Beskrivning är tom.')
    : !promptReady ? 'Prompten är inte ifylld i Promptinställningar.' : '';
  const commit = () => {
    if (!canEdit || tooLong || draft === value) return;
    onSave(draft);
  };

  return (
    <Box sx={suggestion !== undefined ? { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 1.25, alignItems: 'start' } : undefined}>
      <Box>
      {suggestion !== undefined && <Typography sx={[overlineSx, { mb: 0.5 }]}>Nu</Typography>}
      <TextField
        fullWidth
        multiline
        minRows={minRows}
        size="small"
        label={label}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === 'Escape') setDraft(value); }}
        error={tooLong}
        InputProps={{ readOnly: !canEdit }}
        sx={{ bgcolor: launchColors.card }}
      />
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
        <Counter length={draft.length} limit={limit} />
        {tooLong && <Typography component="span" sx={{ fontSize: 12, color: launchColors.dangerText }}>Korta texten för att kunna spara.</Typography>}
        <Box sx={{ flex: 1 }} />
        {onCopied && <CopyButton label={label} text={draft} onCopied={onCopied} />}
        <Button size="small" startIcon={<HistoryIcon />} onClick={onHistory}>Historik</Button>
        {canEdit && (
          <span title={generateBlocked || undefined}>
            <Button
              size="small"
              variant="outlined"
              startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeOutlinedIcon />}
              disabled={Boolean(generateBlocked) || generating}
              onClick={() => onGenerate(draft)}
            >
              {generating ? 'Skriver…' : 'Gör ny text'}
            </Button>
          </span>
        )}
      </Stack>
      {generating && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress sx={{ height: 2, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Skriver förslag… Det kan ta upp till en minut.</Typography>
        </Box>
      )}
      </Box>
      {suggestion !== undefined && (
        <Box
          role="region"
          aria-label={`Förslag på ${label.toLocaleLowerCase('sv')}`}
          sx={{ mt: 1, p: 1.25, borderRadius: '10px', bgcolor: launchColors.softNavy, border: `1px solid ${launchColors.navy}` }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
            <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: launchColors.navy }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy, flex: 1 }}>Förslag</Typography>
            <Counter length={suggestion.length} limit={limit} />
          </Stack>
          {suggestionNote && <Typography sx={{ fontSize: 12, color: launchColors.dangerText, mb: 0.5 }}>{suggestionNote}</Typography>}
          <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: launchColors.text }}>{suggestion}</Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button size="small" onClick={onDismissSuggestion}>Avbryt</Button>
            {suggestion.length > limit ? (
              <Button size="small" variant="contained" onClick={() => { setDraft(suggestion); onDismissSuggestion(); }}>Lägg i rutan och korta</Button>
            ) : (
              <Button size="small" variant="contained" disabled={!suggestion.trim()} onClick={() => onUseSuggestion(suggestion)}>Använd</Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default GeneratedTextField;
